import { computed, makeObservable, observable } from 'mobx';
import type { Aggregate } from 'edit/Aggregate';
import type { OutputMatcher, OutputMatcherFile, OutputMatcherProceed } from 'api/node_definitions/endpoints/types';
import type { EditableNodeDefinition } from './EditableNodeDefinition';

/**
 * How a matched pattern supplies `new_exit_code` on the wire. The wire has no Success/Fail enum — only a nullable integer
 * that the backend maps to status (`0` → completed, non-zero → failed, `null` → leave the agent's exit/status alone). The UI
 * splits that into Success (always exit 0, field disabled and showing 0) vs Fail (editable: empty or any non-zero integer —
 * `0` is not a Fail value because the backend treats it as completed).
 */
export type OverrideStatus = 'success' | 'fail';

const DEFAULT_OUTPUT_FILE: OutputMatcherFile = 'stdout.log';
const DEFAULT_OVERRIDE_STATUS: OverrideStatus = 'fail';
const DEFAULT_PROCEED: OutputMatcherProceed = 'continue';

// Monotonic source of stable per-instance keys for React list rendering / drag ids. New matchers have no server id, and array
// index is unstable across add/remove/reorder, so each instance gets a unique key at construction that never changes.
let nextOutputMatcherKey = 0;

/**
 * One output matcher, as a reactive child of an `EditableNodeDefinition`. The parent→child relationship is owned by a shared
 * `Aggregate` that both sides hold; this child routes its self-delete through it.
 *
 * User-fillable fields with no logic are plain public observables — read `matcher.outputFile`, write `matcher.outputFile = x`.
 * A field only earns an accessor pair when there's behaviour to hide: the Fail return code is kept even while Success is
 * selected (the form shows 0 and disables the field, without clearing the Fail code) so toggling Success↔Fail doesn't lose a
 * typed code, and entering `0` in Fail mode switches to Success to match backend status mapping. `toSave` reads only what the
 * chosen override implies.
 */
export class EditableOutputMatcher {
  private readonly _aggregate: Aggregate<EditableNodeDefinition, EditableOutputMatcher>;
  caseInsensitive = false;
  /** Return code shown/edited for Fail (`null` or non-zero). Not used while Success is selected (UI shows 0). */
  private _customReturnCode: number | null = null;
  /** Stable identity for React list keys / drag ids; never changes after construction. */
  readonly key = `output-matcher-${(nextOutputMatcherKey += 1)}`;
  outputFile: OutputMatcherFile = DEFAULT_OUTPUT_FILE;
  overrideStatus: OverrideStatus = DEFAULT_OVERRIDE_STATUS;
  proceed: OutputMatcherProceed = DEFAULT_PROCEED;
  searchString: string | null = null;
  useRegex = false;

  /**
   * @param aggregate the shared parent→children owner; self-delete routes through it.
   * @param wire when present, seeds the fields from a loaded matcher; when absent, the field defaults stand (a fresh Fail
   *   matcher with an empty return code = keep agent exit/status).
   */
  constructor(aggregate: Aggregate<EditableNodeDefinition, EditableOutputMatcher>, wire?: OutputMatcher) {
    this._aggregate = aggregate;
    makeObservable<EditableOutputMatcher, '_customReturnCode'>(this, {
      caseInsensitive: observable,
      _customReturnCode: observable,
      outputFile: observable,
      overrideStatus: observable,
      proceed: observable,
      searchString: observable,
      useRegex: observable,
    });
    if (wire !== undefined) {
      this.outputFile = wire.output_file;
      this.searchString = wire.search_string;
      this.caseInsensitive = wire.case_insensitive ?? false;
      this.useRegex = wire.use_regex ?? false;
      this.proceed = wire.proceed ?? DEFAULT_PROCEED;
      if (wire.new_exit_code === 0) {
        this.overrideStatus = 'success';
      } else {
        // Non-zero or null -> Fail mode. Null means keep the agent's exit/status on match.
        this.overrideStatus = 'fail';
        this._customReturnCode = wire.new_exit_code ?? null;
      }
    }
  }

  /**
   * Value shown in the return-code field. Success always displays 0 (field is disabled); Fail shows the typed code
   * (`null` or non-zero — never 0 in Fail mode).
   */
  get customReturnCode(): number | null {
    return this.overrideStatus === 'success' ? 0 : this._customReturnCode;
  }

  /**
   * Empty (`null`) and any non-zero integer are kept in Fail mode. `0` switches to Success because the backend maps exit 0
   * to completed. Callers just assign `matcher.customReturnCode = x`; the Fail/Success bookkeeping stays internal.
   */
  set customReturnCode(value: number | null) {
    if (value === 0) {
      this.overrideStatus = 'success';
      return;
    }
    this._customReturnCode = value;
  }

  /**
   * A one-line plain-language summary of the matcher, or `null` when there's no search string yet. Presentational — the tab
   * renders it as the card subtitle.
   */
  get summary(): string | null {
    if (!this.searchString) return null;
    const code = this._customReturnCode;
    const outcome = this.overrideStatus === 'fail' ? (code !== null ? `Fail (${code})` : 'Fail') : 'Success';
    return `${outcome} when ${this.outputFile} contains "${this.searchString}"`;
  }

  /** Remove this matcher from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape. Success -> exit 0; Fail -> the typed return code (`null` keeps agent exit/status; non-zero
   * only — Fail never persists 0). `sort_order` is omitted — array position conveys order.
   */
  toSave(): OutputMatcher {
    if (this.overrideStatus === 'success') {
      return {
        output_file: this.outputFile,
        search_string: this.searchString ?? '',
        case_insensitive: this.caseInsensitive,
        use_regex: this.useRegex,
        new_exit_code: 0,
        proceed: this.proceed,
      };
    }
    const code = this._customReturnCode;
    return {
      output_file: this.outputFile,
      search_string: this.searchString ?? '',
      case_insensitive: this.caseInsensitive,
      use_regex: this.useRegex,
      // Defensive: never send 0 from Fail mode (would be completed on the backend).
      new_exit_code: code === 0 ? null : code,
      proceed: this.proceed,
    };
  }
}
